import { useQuery } from '@tanstack/react-query';

import { getSlotLengthMismatch } from '~/pages/Marketing/AppOnlyFlash/appOnlyFlashCalendar';

import OffersService, { EligibleAppExclusiveOffersResponse } from '~/services/OffersService';

import { eligibleAppExclusiveOfferQueryKeys } from './useGetEligibleAppExclusiveOffers';

// The eligible lookup searches by name (ILIKE), so a name search returns the offer plus a few others.
const NAME_SEARCH_LIMIT = 50;
// svc-offer's max limit. Used without a name, and to retry a name search that matched more than one page.
const MAX_SEARCH_LIMIT = 500;

export interface AppExclusiveSlotCheckParams {
  region: string;
  /** Sydney calendar day, YYYY-MM-DD. */
  date: string;
  /** Length of the slot in days (1 = 24 hours, 2 = 48 hours). */
  days: number;
  offerId: string;
  /** Narrows the eligible lookup; the offer is still matched by id. */
  offerName?: string | null;
}

/**
 * The eligible lookup for one offer on one slot and region. svc-offer only enforces the slot shape and
 * the daily deal limit on write, so moves and copies check "on sale for the slot plus the 3 days after"
 * here first, the same way Add deal does. Throws when svc-offer returns a slot of another length (an older
 * svc-offer ignores `days`), so a 48 hour deal is never re-created as 24 hours.
 */
export async function fetchAppExclusiveSlotCheck(
  { region, date, days, offerId, offerName }: AppExclusiveSlotCheckParams,
  { signal }: { signal?: AbortSignal } = {},
): Promise<EligibleAppExclusiveOffersResponse> {
  const params = offerName
    ? { region, date, days, q: offerName, limit: NAME_SEARCH_LIMIT }
    : { region, date, days, limit: MAX_SEARCH_LIMIT };
  const eligible = await OffersService.getEligibleAppExclusiveOffers(params, { signal });
  const lengthMismatch = getSlotLengthMismatch(eligible, days);
  if (lengthMismatch) {
    throw new Error(lengthMismatch);
  }
  // A generic name can match more offers than the first page holds; look at the full list before
  // concluding the offer isn't eligible. getSlotBlockReason still flags a list that stays cut off.
  const truncated = eligible.total > eligible.offers.length;
  if (truncated && params.limit < MAX_SEARCH_LIMIT && !eligible.offers.some((offer) => offer.offerId === offerId)) {
    return OffersService.getEligibleAppExclusiveOffers({ ...params, limit: MAX_SEARCH_LIMIT }, { signal });
  }
  return eligible;
}

export function useGetAppExclusiveSlotCheck(params: AppExclusiveSlotCheckParams, enabled = true) {
  return useQuery({
    queryKey: [...eligibleAppExclusiveOfferQueryKeys.all, 'slot-check', params] as const,
    queryFn: ({ signal }) => fetchAppExclusiveSlotCheck(params, { signal }),
    enabled: enabled && !!params.region && !!params.date && !!params.offerId,
  });
}
